import { createContext, useContext, useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const AudioContext = createContext();

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AudioProvider = ({ children }) => {
  const { token } = useAuth();
  const [currentAudio, setCurrentAudio] = useState(null); // { theme, mode }
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [mode, setMode] = useState('zen'); // 'zen' or 'movement'
  const [showJournalPrompt, setShowJournalPrompt] = useState(false);
  const [completedAudio, setCompletedAudio] = useState(null);
  const [isBuffering, setIsBuffering] = useState(false);
  const audioRef = useRef(new Audio());

  // Sauvegarder la progression toutes les 5 secondes
  useEffect(() => {
    if (currentAudio && isPlaying) {
      const interval = setInterval(() => {
        saveProgress();
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [currentAudio, isPlaying, currentTime]);

  // Handle audio ended event - using ref to avoid stale closure
  const handleAudioEnded = useCallback(() => {
    setIsPlaying(false);
    saveProgress(true); // Marquer comme complété
  }, []);

  // Setup audio element
  useEffect(() => {
    const audio = audioRef.current;

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
    };

    const handleEnded = () => {
      handleAudioEnded();
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [handleAudioEnded]);

  // Trigger journal prompt when audio ends
  useEffect(() => {
    const audio = audioRef.current;
    
    const triggerJournalPrompt = () => {
      if (currentAudio) {
        setCompletedAudio(currentAudio);
        setShowJournalPrompt(true);
      }
    };
    
    audio.addEventListener('ended', triggerJournalPrompt);
    return () => audio.removeEventListener('ended', triggerJournalPrompt);
  }, [currentAudio]);

  const loadAudio = async (theme, audioMode) => {
    try {
      setIsBuffering(true);
      // Récupérer l'URL de streaming
      const response = await axios.get(
        `${API_URL}/audio-url/${theme.slug}/${audioMode}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const streamUrl = response.data.url;
      
      const audioResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}${streamUrl}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (!audioResponse.ok) {
        throw new Error(`Erreur audio: ${audioResponse.status}`);
      }
      
      const audioBlob = await audioResponse.blob();
      const blobUrl = URL.createObjectURL(audioBlob);
      
      if (audioRef.current.src && audioRef.current.src.startsWith('blob:')) {
        URL.revokeObjectURL(audioRef.current.src);
      }

      audioRef.current.src = blobUrl;
      setCurrentAudio({ theme, mode: audioMode });
      setMode(audioMode);

      const progress = theme.progress?.[audioMode];
      if (progress && progress.position > 0) {
        audioRef.current.currentTime = progress.position;
      }

      setIsBuffering(false);
      return true;
    } catch (error) {
      console.error('Erreur chargement audio:', error);
      setIsBuffering(false);
      return false;
    }
  };

  const play = () => {
    audioRef.current.play();
    setIsPlaying(true);
  };

  const pause = () => {
    audioRef.current.pause();
    setIsPlaying(false);
    saveProgress();
  };

  const seek = (time) => {
    audioRef.current.currentTime = time;
    setCurrentTime(time);
  };

  const saveProgress = async (completed = false) => {
    if (!currentAudio || !token) return;

    try {
      // Considérer comme terminé si on a écouté au moins 90% de l'audio
      const isCompleted = completed || 
        (audioRef.current.duration > 0 && 
         audioRef.current.currentTime >= audioRef.current.duration * 0.9);
      
      await axios.post(
        `${API_URL}/progress`,
        {
          theme_slug: currentAudio.theme.slug,
          mode: currentAudio.mode,
          position: audioRef.current.currentTime,
          completed: isCompleted
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Erreur sauvegarde progression:', error);
    }
  };

  const closeJournalPrompt = useCallback(() => {
    setShowJournalPrompt(false);
    setCompletedAudio(null);
  }, []);

  const closePlayer = useCallback(() => {
    // Sauvegarder la progression avant de fermer
    saveProgress();
    // Arrêter l'audio
    audioRef.current.pause();
    // Révoquer le blob URL pour libérer la mémoire
    if (audioRef.current.src && audioRef.current.src.startsWith('blob:')) {
      URL.revokeObjectURL(audioRef.current.src);
    }
    audioRef.current.src = '';
    // Réinitialiser l'état
    setCurrentAudio(null);
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
  }, []);

  return (
    <AudioContext.Provider
      value={{
        currentAudio,
        isPlaying,
        isBuffering,
        currentTime,
        duration,
        mode,
        loadAudio,
        play,
        pause,
        seek,
        saveProgress,
        showJournalPrompt,
        completedAudio,
        closeJournalPrompt,
        closePlayer
      }}
    >
      {children}
    </AudioContext.Provider>
  );
};

export const useAudio = () => {
  const context = useContext(AudioContext);
  if (!context) {
    throw new Error('useAudio doit être utilisé dans AudioProvider');
  }
  return context;
};